import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Calendar, CheckCircle, XCircle, AlertTriangle, Building, Package, MapPin, Tag, User, Clock, FileText, Download, Eye } from 'lucide-react';
import axios from 'axios';

const EquipmentCheck = () => {
  const { id } = useParams();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;
  console.log("sprawdzany ekwipunek", equipment)

  useEffect(() => {
    fetchEquipment();
  }, [id]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Public endpoint - no authentication needed
      const response = await axios.get(`${API_BASE_URL}/checkequipment/${id}/`);
      const equipmentData = response.data;
      
      // Try to fetch inspections data like ventroppe app does
      try {
        // Try multiple endpoints like ventroppe app
        const inspectionEndpoints = [
          `${API_BASE_URL}/inspection/?safety_equipment=${id}`,
          `${API_BASE_URL}/inspections/?safety_equipment=${id}`,
          `${API_BASE_URL}/safetyequipmentinspection/?safety_equipment=${id}`,
          `${API_BASE_URL}/equipmentinspection/?equipment_id=${id}`
        ];
        
        let inspectionsData = [];
        for (const endpoint of inspectionEndpoints) {
          try {
            const inspectionResponse = await axios.get(endpoint);
            const data = inspectionResponse.data;
            
            // Handle both direct array response and paginated response
            if (Array.isArray(data)) {
              inspectionsData = data;
            } else if (data && data.results && Array.isArray(data.results)) {
              inspectionsData = data.results;
            }
            
            if (inspectionsData.length > 0) {
              break; // Found data, stop trying other endpoints
            }
          } catch (endpointError) {
            console.log(`Endpoint ${endpoint} failed:`, endpointError.message);
            continue; // Try next endpoint
          }
        }
        
        if (inspectionsData.length > 0) {
          equipmentData.inspections = inspectionsData;
          equipmentData.latest_inspection = inspectionsData[0]; // Most recent
        }
        
      } catch (inspectionError) {
        console.log('Could not fetch inspections:', inspectionError.message);
        // Continue without inspections data
      }
      
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setError('Equipment not found or not accessible');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-white text-green-600 font-bold';
      case 'issued':
        return 'bg-white text-purple-600 font-bold';
      case 'service':
        return 'bg-white text-orange-600 font-bold';
      case 'reservation':
        return 'bg-white text-blue-600 font-bold';
      case 'damaged':
        return 'bg-white text-red-600 font-bold';
      default:
        return 'bg-white text-gray-600 font-bold';
    }
  };

  const getInspectionStatus = (equipment) => {
    if (!equipment.latest_inspection) {
      return { status: 'no-inspection', color: 'text-gray-500', icon: AlertTriangle };
    }

    const validityDate = new Date(equipment.latest_inspection.validity_date);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));

    if (equipment.latest_inspection.status === 'non-ok') {
      return { status: 'failed', color: 'text-red-600', icon: XCircle };
    } else if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'text-red-600', icon: XCircle };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', color: 'text-orange-600', icon: AlertTriangle };
    } else {
      return { status: 'valid', color: 'text-green-600', icon: CheckCircle };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading equipment information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Equipment Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const inspectionStatus = getInspectionStatus(equipment);
  const InspectionIcon = inspectionStatus.icon;

  // Equipment information fields for info tab
  const getEquipmentFields = () => {
    return [
      {
        label: 'Equipment Name',
        value: equipment.safety_equipment_type_name || 'N/A',
        icon: Shield,
        color: 'bg-purple-100 text-purple-600'
      },
      {
        label: 'Brand & Model',
        value: `${equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || 'N/A'} ${equipment.safety_equipment_model_name || ''}`.trim(),
        icon: Building,
        color: 'bg-orange-100 text-orange-600'
      },
      {
        label: 'Serial Number',
        value: equipment.safety_equipment_serial_number || 'N/A',
        icon: Package,
        color: 'bg-green-100 text-green-600'
      },
      {
        label: 'Production Date',
        value: equipment.safety_equipment_manufacture_date ? formatDate(equipment.safety_equipment_manufacture_date) : 'N/A',
        icon: Calendar,
        color: 'bg-indigo-100 text-indigo-600'
      },
      {
        label: 'Inspection Date',
        value: equipment.latest_inspection?.inspection_date ? formatDate(equipment.latest_inspection.inspection_date) : 'Not inspected',
        icon: CheckCircle,
        color: 'bg-green-100 text-green-600'
      },
      {
        label: 'NFC Tag',
        value: equipment.nfc_tag_number || 'No NFC assigned',
        icon: Tag,
        color: 'bg-blue-100 text-blue-600',
        isNFC: true
      }
    ];
  };

  return (
    <div className="min-h-screen bg-white py-2 sm:py-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-4 lg:px-6">
        {/* Header */}
        <div className="mb-6">
          {/* Top bar with logo and app icons */}
          <div className="flex justify-between items-center mb-4">
            {/* Ventro logo */}
            <div className="flex-shrink-0">
              <img 
                src="https://ventro.energy/ventro.png" 
                alt="Ventro Energy Logo" 
                className="h-8 sm:h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* iOS and Android icons */}
            <div className="flex space-x-2">
              <div className="bg-gray-100 rounded-lg p-1.5 sm:p-2 shadow-sm">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.13997 6.91 8.85997 6.88C10.15 6.86 11.36 7.75 12.11 7.75C12.86 7.75 14.28 6.68 15.84 6.84C16.53 6.87 18.47 7.15 19.56 8.83C19.47 8.9 17.96 9.63 17.97 11.43C17.99 13.64 20.06 14.66 20.09 14.67C20.06 14.77 19.67 16.3 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
              </div>
              <div className="bg-gray-100 rounded-lg p-1.5 sm:p-2 shadow-sm">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
              </div>
            </div>
          </div>
          
          {/* Title section */}
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Safety Equipment Check</h1>
          </div>
        </div>

        {/* Equipment Image */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-4 sm:mb-6">
          <div className="h-64 sm:h-80 bg-white flex items-center justify-center p-4">
            {(() => {
              // Priority: equipment picture > model picture (same as mobile app)
              let imageUrl = null;
              
              if (equipment.safety_equipment_picture || equipment.picture) {
                imageUrl = equipment.safety_equipment_picture || equipment.picture;
                console.log('DEBUG: Using equipment picture:', imageUrl);
              } else if (equipment.safety_equipment_model?.image || equipment.safety_equipment_model_image) {
                imageUrl = equipment.safety_equipment_model?.image || equipment.safety_equipment_model_image;
                console.log('DEBUG: Using model image:', imageUrl);
              }
              
              if (!imageUrl) {
                console.log('DEBUG: No image available for equipment ID:', equipment.id);
                return (
                  <div className="text-gray-400">
                    <Package className="h-16 w-16 mx-auto mb-2" />
                    <p className="text-sm">No image available</p>
                  </div>
                );
              }
              
              // If URL doesn't start with http, prepend the base URL (same as mobile app)
              if (!imageUrl.startsWith('http')) {
                // Always use ventro.energy for images, regardless of API URL
                const baseUrl = 'https://ventro.energy';
                imageUrl = imageUrl.startsWith('/') ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`;
                console.log('DEBUG: Using base URL:', baseUrl);
                console.log('DEBUG: Constructed full image URL:', imageUrl);
              }
              
              return (
                <img 
                  src={imageUrl}
                  alt={equipment.safety_equipment_type_name}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    console.log("ERROR: Failed to load equipment image:", e.target.src);
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="text-gray-400">
                        <svg class="h-16 w-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20 6h-2.18l-1.21-2.42A1 1 0 0 0 15.72 3H8.28a1 1 0 0 0-.89.58L6.18 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2zM12 17a5 5 0 1 1 5-5 5 5 0 0 1-5 5z"/>
                        </svg>
                        <p class="text-sm">Image failed to load</p>
                      </div>
                    `;
                  }}
                />
              );
            })()}
          </div>
        </div>

        {/* Main Content with Tabs */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              {[
                { id: 'info', label: 'Info', icon: Package },
                { id: 'specs', label: 'Specification', icon: FileText },
                { id: 'history', label: 'History', icon: Clock }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group inline-flex flex-1 justify-center items-center py-3 sm:py-4 px-2 sm:px-6 border-b-2 font-medium text-xs sm:text-sm ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'info' && (
              <div className="space-y-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Equipment Information</h2>
                
                {/* Equipment Info Table */}
                <div className="bg-gray-50 rounded-lg overflow-x-auto">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      {getEquipmentFields().map((field, index) => {
                        const Icon = field.icon;
                        return (
                          <tr key={index} className="hover:bg-gray-100">
                            <td className="px-2 sm:px-4 py-2 sm:py-3 w-10 sm:w-12">
                              <div className={`p-1.5 sm:p-2 rounded-lg ${field.color}`}>
                                <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-gray-900 text-xs sm:text-sm whitespace-nowrap">
                              {field.label}
                            </td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-700 text-xs sm:text-sm">
                              {field.isStatus ? (
                                <span className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(field.value)}`}>
                                  {field.value}
                                </span>
                              ) : field.isNFC ? (
                                <div className="flex items-center space-x-2">
                                  <span className="font-mono text-xs sm:text-sm bg-gray-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                                    {field.value}
                                  </span>
                                </div>
                              ) : (
                                field.value
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'specs' && (
              <div className="space-y-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Documents & Specifications</h2>
                
                {/* Real specification data */}
                <div className="space-y-3">
                  {(equipment.safety_equipment_model?.specification_file || equipment.safety_equipment_model_specification_file) && (
                    <div className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                      <div className="flex items-center space-x-3" onClick={() => {
                        const specFile = equipment.safety_equipment_model?.specification_file || equipment.safety_equipment_model_specification_file;
                        let specUrl = specFile;
                        
                        // If URL doesn't start with http, prepend the base URL
                        if (specUrl && !specUrl.startsWith('http')) {
                          const baseUrl = 'https://ventro.energy';
                          specUrl = specUrl.startsWith('/') ? `${baseUrl}${specUrl}` : `${baseUrl}/${specUrl}`;
                        }
                        
                        if (specUrl) {
                          window.open(specUrl, '_blank');
                        }
                      }}>
                        <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Equipment Specification</p>
                          <p className="text-sm text-gray-500">PDF • Technical specifications • Click to open</p>
                        </div>
                        <div className="text-gray-400">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {equipment.safety_equipment_comment && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Additional Information</p>
                          <p className="text-sm text-gray-500">{equipment.safety_equipment_comment}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(!equipment.safety_equipment_model?.specification_file && !equipment.safety_equipment_model_specification_file && !equipment.safety_equipment_comment) && (
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No specifications available</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Inspection History</h2>
                
                
                {/* Full Inspection History */}
                {equipment.inspections && equipment.inspections.length > 0 ? (
                  <div className="space-y-2">
                    {equipment.inspections.map((inspection, index) => {
                      const isLatest = index === 0;
                      const validityDate = new Date(inspection.validity_date);
                      const today = new Date();
                      const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <div 
                          key={inspection.id || index} 
                          className={`p-3 rounded-lg border transition-colors hover:shadow-sm ${
                            isLatest 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`p-1 rounded ${inspection.status === 'non-ok' || daysUntilExpiry < 0 ? 'bg-red-100' : daysUntilExpiry <= 30 ? 'bg-amber-100' : 'bg-green-100'}`}>
                                {inspection.status === 'non-ok' || daysUntilExpiry < 0 ? 
                                  <XCircle className="h-3 w-3 text-red-600" /> : 
                                  daysUntilExpiry <= 30 ? 
                                  <AlertTriangle className="h-3 w-3 text-amber-600" /> : 
                                  <CheckCircle className="h-3 w-3 text-green-600" />
                                }
                              </div>
                              
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatDate(inspection.inspection_date)}
                                  </span>
                                  {isLatest && (
                                    <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                                      Latest
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500">
                                  Valid until {formatDate(inspection.validity_date)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              {inspection.inspection_file && (
                                <button
                                  onClick={() => {
                                    const fileUrl = inspection.inspection_file.startsWith('http') 
                                      ? inspection.inspection_file 
                                      : `https://ventro.energy${inspection.inspection_file.startsWith('/') ? '' : '/'}${inspection.inspection_file}`;
                                    window.open(fileUrl, '_blank');
                                  }}
                                  className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                                  title="Download Report"
                                >
                                  <Download className="h-3 w-3" />
                                </button>
                              )}
                              
                              {inspection.inspection_photo && (
                                <button
                                  onClick={() => {
                                    const photoUrl = inspection.inspection_photo.startsWith('http') ? inspection.inspection_photo : `https://ventro.energy${inspection.inspection_photo.startsWith('/') ? '' : '/'}${inspection.inspection_photo}`;
                                    window.open(photoUrl, '_blank');
                                  }}
                                  className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                                  title="View Photo"
                                >
                                  <Eye className="h-3 w-3" />
                                </button>
                              )}
                              
                              <span className={`text-xs px-2 py-1 rounded font-medium ${
                                inspection.status === 'non-ok' ? 'bg-red-100 text-red-700' :
                                daysUntilExpiry < 0 ? 'bg-red-100 text-red-700' :
                                daysUntilExpiry <= 30 ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {inspection.status === 'non-ok' ? 'Failed' :
                                 daysUntilExpiry < 0 ? 'Expired' :
                                 daysUntilExpiry <= 30 ? 'Expires Soon' : 'Valid'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Details Row */}
                          <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                            <span>Inspector: {inspection.inspector || 'N/A'}</span>
                            <span>{inspection.status === 'non-ok' ? 'Failed' : 'Passed'}</span>
                          </div>
                          
                          {inspection.comments && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700">
                              {inspection.comments}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : equipment.latest_inspection ? (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-1 rounded ${
                          inspectionStatus.color === 'text-green-600' ? 'bg-green-100' :
                          inspectionStatus.color === 'text-red-600' ? 'bg-red-100' :
                          inspectionStatus.color === 'text-orange-600' ? 'bg-amber-100' :
                          'bg-gray-100'
                        }`}>
                          <InspectionIcon className="h-3 w-3" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formatDate(equipment.latest_inspection.inspection_date)}
                            </span>
                            <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                              Latest
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Valid until {formatDate(equipment.latest_inspection.validity_date)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {equipment.latest_inspection.inspection_file && (
                          <button
                            onClick={() => {
                              const fileUrl = equipment.latest_inspection.inspection_file.startsWith('http') 
                                ? equipment.latest_inspection.inspection_file 
                                : `https://ventro.energy${equipment.latest_inspection.inspection_file.startsWith('/') ? '' : '/'}${equipment.latest_inspection.inspection_file}`;
                              window.open(fileUrl, '_blank');
                            }}
                            className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                            title="Download Report"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        )}
                        
                        {equipment.latest_inspection.inspection_photo && (
                          <button
                            onClick={() => {
                              const photoUrl = equipment.latest_inspection.inspection_photo.startsWith('http') ? equipment.latest_inspection.inspection_photo : `https://ventro.energy${equipment.latest_inspection.inspection_photo.startsWith('/') ? '' : '/'}${equipment.latest_inspection.inspection_photo}`;
                              window.open(photoUrl, '_blank');
                            }}
                            className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                            title="View Photo"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                        )}
                        
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          inspectionStatus.status === 'valid' ? 'bg-green-100 text-green-700' :
                          inspectionStatus.status === 'expired' || inspectionStatus.status === 'failed' ? 'bg-red-100 text-red-700' :
                          inspectionStatus.status === 'expiring' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {inspectionStatus.status === 'valid' ? 'Valid' :
                           inspectionStatus.status === 'expired' ? 'Expired' :
                           inspectionStatus.status === 'expiring' ? 'Expires Soon' :
                           inspectionStatus.status === 'failed' ? 'Failed' : 'Unknown'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                      <span>Inspector: {equipment.latest_inspection.inspector || 'N/A'}</span>
                      <span>{equipment.latest_inspection.status === 'non-ok' ? 'Failed' : 'Passed'}</span>
                    </div>
                    
                    {equipment.latest_inspection.comments && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700">
                        {equipment.latest_inspection.comments}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-6 sm:p-8 rounded-lg text-center">
                    <AlertTriangle className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm sm:text-base text-gray-500">No inspection history available</p>
                    <p className="text-xs text-gray-400 mt-1 sm:mt-2">
                      Inspection data may not be publicly accessible
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 sm:mt-12 mb-4 border-t border-gray-200 pt-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="h-8 w-px bg-gray-300"></div>
              <p className="text-xs sm:text-sm text-gray-500">Powered by</p>
              <div className="h-8 w-px bg-gray-300"></div>
            </div>
            <a 
              href="https://ventro.energy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://ventro.energy/ventro.png" 
                alt="Ventro Energy" 
                className="h-6 sm:h-8 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-orange-600 font-bold text-sm sm:text-base">ventro.energy</span>';
                }}
              />
            </a>
            <p className="text-xs text-gray-400 mt-2">
              © {new Date().getFullYear()} Ventro Energy. All rights reserved.
            </p>
            <div className="flex items-center justify-center space-x-4 mt-3">
              <a 
                href="https://apps.apple.com/app/ventro-ppe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.13997 6.91 8.85997 6.88C10.15 6.86 11.36 7.75 12.11 7.75C12.86 7.75 14.28 6.68 15.84 6.84C16.53 6.87 18.47 7.15 19.56 8.83C19.47 8.9 17.96 9.63 17.97 11.43C17.99 13.64 20.06 14.66 20.09 14.67C20.06 14.77 19.67 16.3 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                <span>Download for iOS</span>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.ventro.ppe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <span>Download for Android</span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EquipmentCheck;